import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Heart, ArrowLeft, Eye, EyeOff } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate 12-hour time options
const generateTimeOptions = () => {
  const times = [];
  for (let hour = 1; hour <= 12; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const minuteStr = minute.toString().padStart(2, "0");
      times.push(`${hour}:${minuteStr} AM`);
    }
  }
  for (let hour = 1; hour <= 12; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const minuteStr = minute.toString().padStart(2, "0");
      times.push(`${hour}:${minuteStr} PM`);
    }
  }
  return times;
};

const timeOptions = generateTimeOptions();

// HUD Corner Brackets Component
const HUDCorners = () => (
  <>
    <div className="hud-corner-tl" />
    <div className="hud-corner-tr" />
    <div className="hud-corner-bl" />
    <div className="hud-corner-br" />
  </>
);

// Header Component
const Header = () => (
  <header className="w-full px-4 py-3 flex items-center justify-between border-b border-[#FF1B1C]/20">
    <a
      href="https://cardiac-solutions.ai/hub"
      data-testid="back-to-hub-btn"
      className="font-orbitron text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] hover:text-[#FF1B1C] transition-all duration-150 flex items-center gap-2"
    >
      <ArrowLeft className="w-3 h-3" />
      Back To Hub
    </a>

    <div className="flex flex-col items-center">
      <div className="flex items-center gap-2">
        <Heart
          data-testid="heart-icon"
          className="w-6 h-6 text-[#FF1B1C] heart-pulse fill-[#FF1B1C]"
        />
        <h1 className="font-orbitron text-xl sm:text-2xl tracking-tight uppercase font-bold">
          <span className="text-white">Cardiac</span>{" "}
          <span className="text-[#FF1B1C]">Solutions</span>
        </h1>
      </div>
      <span className="font-mono text-[10px] text-[#A1A1AA] tracking-widest uppercase mt-1">
        Backend Monitoring and Control
      </span>
    </div>

    <div className="w-[100px]" /> {/* Spacer for centering */}
  </header>
);

// Readiness Card Component
const ReadinessCard = ({ config, onSave, loading }) => {
  const [autoLoginTime, setAutoLoginTime] = useState(config?.auto_login_time || "");
  const [username, setUsername] = useState(config?.username || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (config) {
      setAutoLoginTime(config.auto_login_time || "");
      setUsername(config.username || "");
    }
  }, [config]);

  const handleSave = async () => {
    if (!autoLoginTime || !username) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      await onSave({
        auto_login_time: autoLoginTime,
        username,
        ...(password && { password }),
      });
      toast.success("Configuration saved successfully");
      setPassword("");
    } catch (error) {
      toast.error("Failed to save configuration");
    }
    setSaving(false);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "-- : -- : --";
    const date = new Date(isoString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  return (
    <div
      data-testid="readiness-card"
      className="relative bg-[#0A0A0A]/90 backdrop-blur-md border border-[#FF1B1C]/30 p-4 glow-red stark-card"
    >
      <HUDCorners />

      {/* Card Title */}
      <h2 className="font-orbitron text-base sm:text-lg uppercase font-semibold tracking-wider text-white mb-4">
        Readiness
      </h2>

      <div className="space-y-3">
        {/* Auto Login Time */}
        <div className="space-y-1">
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
            Auto Login Time
          </label>
          <Select
            data-testid="auto-login-select"
            value={autoLoginTime}
            onValueChange={setAutoLoginTime}
          >
            <SelectTrigger
              data-testid="auto-login-trigger"
              className="w-full h-8 bg-black/50 border border-[#FF1B1C]/30 text-white font-mono text-sm rounded-none focus:border-[#FF1B1C] focus:ring-1 focus:ring-[#FF1B1C]/50"
            >
              <SelectValue placeholder="Select time..." />
            </SelectTrigger>
            <SelectContent className="bg-[#111111] border border-[#FF1B1C]/30 rounded-none max-h-[200px]">
              {timeOptions.map((time) => (
                <SelectItem
                  key={time}
                  value={time}
                  className="font-mono text-white focus:bg-[#FF1B1C]/20 focus:text-white rounded-none cursor-pointer"
                >
                  {time}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Username */}
        <div className="space-y-1">
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
            User Name
          </label>
          <Input
            data-testid="username-input"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Enter username..."
            className="w-full h-8 bg-black/50 border border-[#FF1B1C]/30 text-white font-mono text-sm rounded-none focus:border-[#FF1B1C] focus:ring-1 focus:ring-[#FF1B1C]/50 placeholder:text-[#A1A1AA]/50"
          />
        </div>

        {/* Password */}
        <div className="space-y-1">
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
            Password
          </label>
          <div className="relative">
            <Input
              data-testid="password-input"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={config ? "Enter new password to update..." : "Enter password..."}
              className="w-full h-8 bg-black/50 border border-[#FF1B1C]/30 text-white font-mono text-sm rounded-none focus:border-[#FF1B1C] focus:ring-1 focus:ring-[#FF1B1C]/50 placeholder:text-[#A1A1AA]/50 pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-[#FF1B1C] transition-colors"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-[#FF1B1C]/20 my-3" />

        {/* Status Fields */}
        <div className="space-y-2">
          <div className="space-y-1">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
              Last Login
            </label>
            <div
              data-testid="last-login-display"
              className="font-mono text-xs text-white bg-black/30 border border-[#FF1B1C]/20 p-2"
            >
              <span className="text-[#00E5FF]">SYSTEM_STATUS:</span>{" "}
              {loading ? "LOADING..." : formatDateTime(config?.last_login)}
            </div>
          </div>

          <div className="space-y-1">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
              Next Login
            </label>
            <div
              data-testid="next-login-display"
              className="font-mono text-xs text-white bg-black/30 border border-[#FF1B1C]/20 p-2"
            >
              <span className="text-[#00E5FF]">SCHEDULED:</span>{" "}
              {loading ? "LOADING..." : formatDateTime(config?.next_login)}
            </div>
          </div>
        </div>

        {/* Save Button */}
        <Button
          data-testid="save-config-btn"
          onClick={handleSave}
          disabled={saving || loading}
          className="w-full mt-3 bg-[#FF1B1C]/10 border border-[#FF1B1C] text-[#FF1B1C] hover:bg-[#FF1B1C] hover:text-black hover:shadow-[0_0_20px_rgba(255,27,28,0.6)] uppercase tracking-widest text-xs font-bold transition-all duration-300 rounded-none font-orbitron h-8"
        >
          {saving ? "Saving..." : config ? "Update Configuration" : "Save Configuration"}
        </Button>
      </div>
    </div>
  );
};

// Cameras Card Component
const CamerasCard = () => {
  const lastImageReceived = new Date().toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

  return (
    <div
      data-testid="cameras-card"
      className="relative bg-[#0A0A0A]/90 backdrop-blur-md border border-[#FF1B1C]/30 p-4 glow-red stark-card"
    >
      <HUDCorners />

      {/* Card Title */}
      <h2 className="font-orbitron text-base sm:text-lg uppercase font-semibold tracking-wider text-white mb-4">
        Cameras
      </h2>

      {/* Status Fields */}
      <div className="space-y-3">
        {/* Total Cameras */}
        <div className="space-y-1">
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
            Total Cameras
          </label>
          <div
            data-testid="total-cameras-display"
            className="font-mono text-xs text-white bg-black/30 border border-[#FF1B1C]/20 p-2 flex items-center justify-between"
          >
            <span className="text-[#00E5FF]">CAMERA_COUNT:</span>
            <span className="text-[#FF1B1C] font-bold text-lg">3,300</span>
          </div>
        </div>

        {/* Last Image Received */}
        <div className="space-y-1">
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
            Last Image Received
          </label>
          <div
            data-testid="last-image-received-display"
            className="font-mono text-xs text-white bg-black/30 border border-[#FF1B1C]/20 p-2"
          >
            <span className="text-[#00E5FF]">TIMESTAMP:</span> {lastImageReceived}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Dashboard Component
const Dashboard = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/readiness`);
      setConfig(response.data);
    } catch (error) {
      console.error("Error fetching config:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleSaveConfig = async (data) => {
    if (config) {
      // Update existing
      const response = await axios.put(`${API}/readiness`, data);
      setConfig(response.data);
    } else {
      // Create new
      const response = await axios.post(`${API}/readiness`, {
        ...data,
        password: data.password || "default",
      });
      setConfig(response.data);
    }
  };

  return (
    <div className="stark-dashboard hud-grid h-screen overflow-hidden flex flex-col">
      <Header />

      <main className="p-4 flex-1">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-5xl mx-auto">
          <ReadinessCard config={config} onSave={handleSaveConfig} loading={loading} />
          <CamerasCard />
        </div>
      </main>

      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#111111",
            border: "1px solid rgba(255, 27, 28, 0.3)",
            color: "#FFFFFF",
            fontFamily: "JetBrains Mono, monospace",
          },
        }}
      />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Dashboard />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
